import { BillingMode, ProductTierId } from './productPlans'
import { isSupabaseConfigured, supabase } from './supabase'

export interface CheckoutResult {
  ok: boolean
  checkoutUrl?: string
  orderId?: string
  mode?: 'checkout' | 'manual'
  message?: string
}

export const startProductCheckout = async (
  tier: ProductTierId,
  billingMode: BillingMode,
  addons: Array<{ id: string }> = [],
  totalAmount?: number,
  spec?: {
    summary?: string
    messages?: Array<{ role: string; content: string }>
    domain?: {
      needsRegistration?: boolean
      desiredDomain?: string
      availabilityStatus?: string
      availabilityMode?: string
      availabilityMessage?: string
      isAvailable?: boolean | null
      isPremium?: boolean | null
      premiumReason?: string
      purchaseAmount?: number
    } | null
    pricing?: {
      basePrice?: number
      addOnOriginalTotal?: number
      addOnDiscountTotal?: number
      addOnTotal?: number
      domainRegistrationTotal?: number
      total?: number
      items?: Array<{
        id: string
        label: string
        originalPrice: number
        adjustedPrice: number
        discountAmount: number
        discountLabel?: string
      }>
    }
  },
): Promise<CheckoutResult> => {
  if (!isSupabaseConfigured) {
    return {
      ok: false,
      message: 'Client accounts are not configured yet. Add the Supabase environment variables before taking orders.',
    }
  }

  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session) {
    const params = new URLSearchParams({ tier, billing: billingMode, mode: 'signup' })
    window.location.href = `/client?${params.toString()}`
    return { ok: false, message: 'Create an account to continue.' }
  }

  const response = await fetch('/api/create-checkout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      tier,
      billingMode,
      addons: addons.map((addon) => addon.id),
      totalAmount,
      spec,
    }),
  })

  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('Checkout API is not running in this local session. Use the deployed site or start the API server to test payments.')
  }

  const result = (await response.json()) as CheckoutResult

  if (!response.ok) {
    throw new Error(result.message || 'Checkout could not be started.')
  }

  if (result.checkoutUrl) {
    window.location.href = result.checkoutUrl
  }

  return result
}
